import { FC, useEffect, useRef } from 'react';

interface ConfirmActionModalProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmActionModal: FC<ConfirmActionModalProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => onCancel();
    dialog.addEventListener('close', handleClose);
    // eslint-disable-next-line consistent-return
    return () => dialog.removeEventListener('close', handleClose);
  }, [onCancel]);

  return (
    <dialog
      ref={dialogRef}
      className="modal"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-message"
    >
      <article className="modal-box flex flex-col gap-6 max-w-md bg-base-200">
        <header>
          <h1
            id="confirm-modal-title"
            className="text-2xl font-semibold text-primary"
          >
            {title}
          </h1>
        </header>
        <section>
          <p id="confirm-modal-message" className="text-lg text-gray-500">
            {message}
          </p>
        </section>
        <footer className="flex justify-end gap-4">
          <button
            type="button"
            className="btn btn-lg px-6 font-semibold"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn btn-error btn-lg text-white px-6 font-semibold"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </footer>
      </article>
    </dialog>
  );
};

export default ConfirmActionModal;
